import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ensureClientViewDraft,
  getClientView,
  listClientViewVersions,
  publishClientViewDraft,
  saveClientViewDraftManifest,
  validateClientViewDraftManifest,
} from "../../api/adminClientViewsApi";
import RequirePermission from "../../auth/RequirePermission";
import { useAuth } from "../../auth/useAuth";
import {
  labelForBasePerspective,
  labelForStatus,
  labelForViewType,
  statusBadgeTone,
} from "../../constants/clientViews";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";
import ClientViewQueryProfilesSection from "../../components/midpoint/clientViews/ClientViewQueryProfilesSection";
import ClientViewTransportConfigSection from "../../components/midpoint/clientViews/ClientViewTransportConfigSection";

function ClientViewDetailPageContent() {
  const { clientViewId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("client_views.manage");
  const canPublish = hasPermission("client_views.publish");

  const [clientView, setClientView] = useState(null);
  const [manifestText, setManifestText] = useState("");
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [changeSummary, setChangeSummary] = useState("");

  const versionSummary = clientView?.versionSummary;

  const loadDetail = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const [detailBody, versionsBody] = await Promise.all([
        getClientView(clientViewId, { includeManifests: true }),
        listClientViewVersions(clientViewId).catch(() => ({ versions: [] })),
      ]);

      const view = detailBody.clientView;
      setClientView(view);
      setVersions(versionsBody.versions || []);

      let manifest = view.draft?.manifest;
      if (!manifest && canManage) {
        const ensured = await ensureClientViewDraft(clientViewId);
        manifest = ensured.version?.manifestJson;
        if (ensured.version) {
          const refreshed = await getClientView(clientViewId, { includeManifests: true });
          setClientView(refreshed.clientView);
          manifest = refreshed.clientView.draft?.manifest || manifest;
        }
      } else if (!manifest && view.published?.manifest) {
        manifest = view.published.manifest;
      }

      setManifestText(JSON.stringify(manifest || {}, null, 2));
    } catch (error) {
      setClientView(null);
      setErrorMessage(error.message || "Unable to load client view.");
    } finally {
      setIsLoading(false);
    }
  }, [canManage, clientViewId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const parseManifestText = () => {
    try {
      return { manifest: JSON.parse(manifestText), parseError: null };
    } catch (error) {
      return { manifest: null, parseError: error.message };
    }
  };

  const handleSaveDraft = async () => {
    if (!canManage) {
      return;
    }

    const { manifest, parseError } = parseManifestText();
    if (parseError) {
      setValidationErrors([{ path: "", message: `Invalid JSON: ${parseError}` }]);
      return;
    }

    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    setValidationErrors([]);

    try {
      await saveClientViewDraftManifest(clientViewId, manifest);
      setSuccessMessage("Draft saved. Public runtime is unchanged until you publish.");
      await loadDetail();
    } catch (error) {
      setErrorMessage(error.message || "Unable to save draft.");
      if (error.body?.errors) {
        setValidationErrors(error.body.errors);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async () => {
    const { manifest, parseError } = parseManifestText();
    if (parseError) {
      setValidationErrors([{ path: "", message: `Invalid JSON: ${parseError}` }]);
      return;
    }

    setValidationErrors([]);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      await validateClientViewDraftManifest(clientViewId, manifest);
      setSuccessMessage("Manifest is valid.");
    } catch (error) {
      setErrorMessage(error.message || "Validation failed.");
      if (error.body?.errors) {
        setValidationErrors(error.body.errors);
      }
    }
  };

  const handlePublish = async () => {
    if (!canPublish) {
      return;
    }

    setIsPublishing(true);
    setErrorMessage("");
    setSuccessMessage("");
    setValidationErrors([]);

    try {
      const { manifest, parseError } = parseManifestText();
      if (parseError) {
        setValidationErrors([{ path: "", message: `Invalid JSON: ${parseError}` }]);
        return;
      }

      await saveClientViewDraftManifest(clientViewId, manifest);
      await publishClientViewDraft(clientViewId, changeSummary.trim() || undefined);
      setSuccessMessage("Published successfully. The public manifest is now live.");
      setChangeSummary("");
      await loadDetail();
    } catch (error) {
      setErrorMessage(error.message || "Publish failed.");
      if (error.body?.errors) {
        setValidationErrors(error.body.errors);
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const statusHint = useMemo(() => {
    if (!versionSummary) {
      return null;
    }
    if (versionSummary.hasUnpublishedChanges) {
      return "Draft has unpublished changes. The public site uses the last published version only.";
    }
    if (versionSummary.hasPublished && !versionSummary.hasDraft) {
      return "No draft in progress. Edits will create a new draft from the published manifest.";
    }
    if (!versionSummary.hasPublished) {
      return "Not published yet. Nothing is exposed on the public manifest endpoint.";
    }
    return "Draft matches the published manifest.";
  }, [versionSummary]);

  return (
    <div className="midpoint-admin-page client-view-detail-page">
      <header className="panel-card client-view-detail-page__header">
        <p className="eyebrow">Client Views</p>
        {isLoading ? <h1>Loading…</h1> : <h1>{clientView?.name || "Client view"}</h1>}
        <div className="client-view-detail-page__badges">
          {clientView ? (
            <StatusBadge
              label={labelForStatus(clientView.status)}
              tone={statusBadgeTone(clientView.status)}
            />
          ) : null}
          {versionSummary?.hasDraft ? (
            <StatusBadge label="Draft" tone="pending" />
          ) : null}
          {versionSummary?.hasPublished ? (
            <StatusBadge label="Published" tone="confirmed" />
          ) : (
            <StatusBadge label="Not published" tone="disputed" />
          )}
          {versionSummary?.hasUnpublishedChanges ? (
            <StatusBadge label="Unpublished changes" tone="pending" />
          ) : null}
        </div>
        {statusHint ? <p className="muted client-view-detail-page__hint">{statusHint}</p> : null}
        <div className="client-view-detail-page__actions">
          <button type="button" className="midpoint-share-button" onClick={() => navigate("/")}>
            Back to map
          </button>
          <Link className="midpoint-share-button" to={`/admin/client-views/${clientViewId}/studio`}>
            Open View Studio
          </Link>
          {clientView ? (
            <>
              <Link className="midpoint-share-button" to={`/v/${clientView.slug}`}>
                Open public runtime
              </Link>
              <Link
                className="midpoint-share-button"
                to={`/client-views/${clientView.slug}/preview`}
              >
                Preview alias
              </Link>
            </>
          ) : null}
        </div>
      </header>

      {successMessage ? (
        <section className="panel-card account-security-flash">
          <p>{successMessage}</p>
        </section>
      ) : null}

      {errorMessage ? (
        <section className="panel-card">
          <p className="admin-data-health-error">{errorMessage}</p>
        </section>
      ) : null}

      {validationErrors.length ? (
        <section className="panel-card">
          <h2>Validation errors</h2>
          <ul className="client-view-validation-errors">
            {validationErrors.map((entry) => (
              <li key={`${entry.path}-${entry.message}`}>
                <strong>{entry.path || "manifest"}</strong>: {entry.message}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {clientView ? (
        <>
          <section className="panel-card">
            <h2>Overview</h2>
            <p className="muted">{clientView.description || "No description yet."}</p>
            <dl className="client-view-detail-page__meta">
              <div>
                <dt>View type</dt>
                <dd>{labelForViewType(clientView.viewType)}</dd>
              </div>
              <div>
                <dt>Base perspective</dt>
                <dd>{labelForBasePerspective(clientView.basePerspective)}</dd>
              </div>
              <div>
                <dt>Slug</dt>
                <dd>/{clientView.slug}</dd>
              </div>
              <div>
                <dt>Public manifest</dt>
                <dd>
                  <code>/api/v1/views/{clientView.slug}/manifest</code>
                </dd>
              </div>
              <div>
                <dt>Public listings</dt>
                <dd>
                  <code>/api/v1/views/{clientView.slug}/listings</code>
                </dd>
              </div>
              <div>
                <dt>Public lead capture</dt>
                <dd>
                  <code>POST /api/v1/views/{clientView.slug}/leads</code>
                </dd>
              </div>
              <div>
                <dt>Public runtime</dt>
                <dd>
                  <code>/v/{clientView.slug}</code>
                </dd>
              </div>
              <div>
                <dt>CRM leads</dt>
                <dd>
                  <Link to={`/crm/leads?clientViewId=${clientViewId}`}>
                    View leads for this Client View
                  </Link>
                </dd>
              </div>
              {versionSummary?.published?.publishedAt ? (
                <div>
                  <dt>Last published</dt>
                  <dd>{formatDateTime(versionSummary.published.publishedAt)}</dd>
                </div>
              ) : null}
              {versionSummary?.draft?.updatedAt ? (
                <div>
                  <dt>Draft updated</dt>
                  <dd>{formatDateTime(versionSummary.draft.updatedAt)}</dd>
                </div>
              ) : null}
            </dl>
          </section>

          <ClientViewQueryProfilesSection
            clientViewId={clientViewId}
            canManage={canManage}
          />

          <ClientViewTransportConfigSection
            clientViewId={clientViewId}
            canManage={canManage}
          />

          <section className="panel-card client-view-detail-page__manifest">
            <h2>Draft manifest</h2>
            <p className="muted">
              Edit the manifest JSON below. Saving updates the draft only; use Publish to push
              changes to the public runtime.
            </p>
            <label className="admin-users-field client-view-detail-page__manifest-editor">
              <span>Manifest JSON (manifestVersion 1)</span>
              <textarea
                rows={18}
                value={manifestText}
                onChange={(event) => setManifestText(event.target.value)}
                disabled={!canManage || isLoading}
                spellCheck={false}
              />
            </label>
            {canManage ? (
              <label className="admin-users-field">
                <span>Publish change summary (optional)</span>
                <input
                  type="text"
                  value={changeSummary}
                  onChange={(event) => setChangeSummary(event.target.value)}
                  placeholder="Describe what changed in this publish"
                />
              </label>
            ) : null}
            <div className="client-view-detail-page__manifest-actions">
              {canManage ? (
                <button
                  type="button"
                  className="report-submit-cta"
                  onClick={handleSaveDraft}
                  disabled={isSaving || isPublishing}
                >
                  {isSaving ? "Saving…" : "Save draft"}
                </button>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  className="midpoint-share-button"
                  onClick={handleValidate}
                  disabled={isSaving || isPublishing}
                >
                  Validate
                </button>
              ) : null}
              {canPublish ? (
                <button
                  type="button"
                  className="report-submit-cta"
                  onClick={handlePublish}
                  disabled={isSaving || isPublishing}
                >
                  {isPublishing ? "Publishing…" : "Publish"}
                </button>
              ) : (
                <p className="muted">Publishing requires the client_views.publish permission.</p>
              )}
            </div>
          </section>

          <section className="panel-card client-view-detail-page__section">
            <h2>Version history</h2>
            {versions.length ? (
              <table className="admin-data-health-table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th>Published</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((version) => (
                    <tr key={version.clientViewVersionId}>
                      <td>v{version.versionNumber}</td>
                      <td>{version.status}</td>
                      <td>{formatDateTime(version.updatedAt)}</td>
                      <td>
                        {version.publishedAt ? formatDateTime(version.publishedAt) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No versions recorded yet.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

const ClientViewDetailPage = () => (
  <RequirePermission permission="client_views.view">
    <ClientViewDetailPageContent />
  </RequirePermission>
);

export default ClientViewDetailPage;
